import { useEffect, useState, useRef } from "react";
import usePartAssembly from "../../data/usePartAssembly";
import { IPartAssembly, IPartAssemblyPartCategories } from "../../models/Part";
import { JobPartCategory } from "../../models/Job";
import JobPart from "./JobPart";
import LoadingWheel from "../shared/LoadingWheel";

// Component to handle indeterminate checkbox state
function IndeterminateCheckbox({
  checked,
  indeterminate,
  onChange,
  className
}: {
  checked: boolean;
  indeterminate: boolean;
  onChange: () => void;
  className?: string;
}) {
  const checkboxRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (checkboxRef.current) {
      checkboxRef.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  return (
    <input
      type="checkbox"
      ref={checkboxRef}
      checked={checked}
      onChange={onChange}
      className={className}
    />
  );
}

export interface JobAssemblyWithParts extends IPartAssembly {
  jobParts: JobPartCategory[];
}

export interface JobPartsByAssemblyProps {
  jobPartCategories: JobPartCategory[];
  markAsRemoved: (partData: JobPartCategory['partData']) => void;
  setDamaged: (partData: JobPartCategory['partData']) => void;
  setDamagedOpen: (open: boolean) => void;
  handlePrint: (partData: JobPartCategory['partData']) => void;
  selectedPartIds: Set<string>;
  togglePartSelection: (partId: string | null | undefined) => void;
  toggleAssemblySelection: (assemblyParts: JobPartCategory[]) => void;
  onDiagramClick?: (partCategory: JobPartCategory) => void;
}

export default function JobPartsByAssembly({
  jobPartCategories,
  markAsRemoved,
  setDamaged,
  setDamagedOpen,
  handlePrint,
  selectedPartIds,
  togglePartSelection,
  toggleAssemblySelection,
  onDiagramClick
}: JobPartsByAssemblyProps) {
  const { assemblies } = usePartAssembly();
  const [isLoading, setIsLoading] = useState(true);
  const [assemblyGroups, setAssemblyGroups] = useState<JobAssemblyWithParts[]>([]);
  const [unmatchedParts, setUnmatchedParts] = useState<JobPartCategory[]>([]);

  useEffect(() => {
    if (!assemblies.isLoading && jobPartCategories) {
      // Find matching parts for each assembly
      const matchedAssemblies: JobAssemblyWithParts[] = assemblies.data?.data.map((assembly: IPartAssembly) => {
        // Create a map of categoryId to order for quick lookup
        const categoryOrderMap = new Map(
          assembly.partAssemblyPartCategories?.map((cat, index) => [cat.partCategoryId, index]) ?? []
        );

        // Filter and sort job parts based on the order in partAssemblyPartCategories
        const matchingJobParts: JobPartCategory[] = jobPartCategories
          .filter(jobPart =>
            assembly.partAssemblyPartCategories?.some((cat: IPartAssemblyPartCategories) =>
              cat.partCategoryId === jobPart.partCategoryId
            )
          )
          .sort((a, b) => {
            const orderA = categoryOrderMap.get(a.partCategoryId!) ?? Number.MAX_SAFE_INTEGER;
            const orderB = categoryOrderMap.get(b.partCategoryId!) ?? Number.MAX_SAFE_INTEGER;
            return orderA - orderB;
          });

        return {
          ...assembly,
          jobParts: matchingJobParts,
        };
      }) ?? [];

      // Filter out job parts that have no matching assemblies
      const unmatchedJobParts = jobPartCategories.filter(jobPart =>
        !matchedAssemblies?.some(assembly =>
          assembly.partAssemblyPartCategories?.some((cat: IPartAssemblyPartCategories) =>
            cat.partCategoryId === jobPart.partCategoryId
          )
        )
      );

      setAssemblyGroups(matchedAssemblies.filter(assembly => assembly.jobParts.length > 0));
      setUnmatchedParts(unmatchedJobParts);
      setIsLoading(false);
    }
  }, [assemblies.isLoading, assemblies.data, jobPartCategories]);

  if (isLoading) {
    return <LoadingWheel />;
  }

  return (
    <div className="space-y-6">
      {/* Assembly Groups */}
      {assemblyGroups.map((assembly) => {
        const assemblyPartIds = assembly.jobParts
          .map(jobPart => jobPart.partData?.id)
          .filter((id): id is string => id !== null && id !== undefined);
        const allSelected = assemblyPartIds.length > 0 && assemblyPartIds.every(id => selectedPartIds.has(id));
        const someSelected = assemblyPartIds.some(id => selectedPartIds.has(id));

        return (
          <div key={assembly.id} className="rounded-lg bg-gray-100 p-4">
            <div className="flex items-center gap-2 mb-4">
              <IndeterminateCheckbox
                checked={allSelected}
                indeterminate={someSelected && !allSelected}
                onChange={() => toggleAssemblySelection(assembly.jobParts)}
                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
              />
              <h3 className="text-lg font-semibold">{assembly.name}</h3>
              {someSelected && (
                <span className="text-sm text-gray-600">
                  ({assemblyPartIds.filter(id => selectedPartIds.has(id)).length} of {assemblyPartIds.length} selected)
                </span>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
              {assembly.jobParts.map((jobPart) => (
                <JobPart
                  key={jobPart.partDataId}
                  partCategory={jobPart}
                  markAsRemoved={markAsRemoved}
                  setDamaged={setDamaged}
                  setDamagedOpen={setDamagedOpen}
                  handlePrint={handlePrint}
                  selectedPartIds={selectedPartIds}
                  togglePartSelection={togglePartSelection}
                  onDiagramClick={onDiagramClick}
                />
              ))}
            </div>
          </div>
        );
      })}

      {/* Unmatched Parts */}
      {unmatchedParts.length > 0 && (() => {
        const unmatchedPartIds = unmatchedParts
          .map(jobPart => jobPart.partData?.id)
          .filter((id): id is string => id !== null && id !== undefined);
        const allUnmatchedSelected = unmatchedPartIds.length > 0 && unmatchedPartIds.every(id => selectedPartIds.has(id));
        const someUnmatchedSelected = unmatchedPartIds.some(id => selectedPartIds.has(id));

        return (
          <div className="rounded-lg bg-gray-100 p-4">
            <div className="flex items-center gap-2 mb-4">
              <IndeterminateCheckbox
                checked={allUnmatchedSelected}
                indeterminate={someUnmatchedSelected && !allUnmatchedSelected}
                onChange={() => toggleAssemblySelection(unmatchedParts)}
                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
              />
              <h3 className="text-lg font-semibold">Non-Assembly Parts</h3>
              {someUnmatchedSelected && (
                <span className="text-sm text-gray-600">
                  ({unmatchedPartIds.filter(id => selectedPartIds.has(id)).length} of {unmatchedPartIds.length} selected)
                </span>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
              {unmatchedParts.map((jobPart) => (
                <JobPart
                  key={jobPart.id}
                  partCategory={jobPart}
                  markAsRemoved={markAsRemoved}
                  setDamaged={setDamaged}
                  setDamagedOpen={setDamagedOpen}
                  handlePrint={handlePrint}
                  selectedPartIds={selectedPartIds}
                  togglePartSelection={togglePartSelection}
                  onDiagramClick={onDiagramClick}
                />
              ))}
            </div>
          </div>
        );
      })()}
    </div>
  );
} 